import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { useOrderStore } from '../../store/orderStore'
import './PricingModule.css'

interface Package {
    id: string
    name: string
    amount: number
    price: number
    features: {
        delivery: string
        walletDuration: string
        transferable: string
        swappable: boolean
        tradable: boolean
        web3Compatible: boolean
        confirmations: string
        additionalFeatures?: string[]
    }
}

export default function PricingModule() {
    const { t } = useTranslation()
    const { selectedPackage, setSelectedPackage, developerAccess, setDeveloperAccess } = useOrderStore()
    const [customAmount, setCustomAmount] = useState<string>('')
    const [showCustom, setShowCustom] = useState(false)

    const packages: Package[] = [
        {
            id: 'starter',
            name: 'Starter Flash',
            amount: 5000,
            price: 500,
            features: {
                delivery: 'Instant',
                walletDuration: '7 days',
                transferable: 'Once',
                swappable: false,
                tradable: false,
                web3Compatible: true,
                confirmations: 'All confirmations included'
            }
        },
        {
            id: 'professional',
            name: 'Professional',
            amount: 25000,
            price: 2500,
            features: {
                delivery: 'Instant',
                walletDuration: '30 days',
                transferable: 'Up to 3 times',
                swappable: true,
                tradable: false,
                web3Compatible: true,
                confirmations: 'All confirmations included',
                additionalFeatures: [
                    'Priority support',
                    'Lower network fees',
                    'Extended validity'
                ]
            }
        },
        {
            id: 'enterprise',
            name: 'Enterprise',
            amount: 100000,
            price: 10000,
            features: {
                delivery: 'Instant',
                walletDuration: 'Unlimited',
                transferable: 'Unlimited',
                swappable: true,
                tradable: true,
                web3Compatible: true,
                confirmations: 'All confirmations included',
                additionalFeatures: [
                    'Enhanced Privacy',
                    'VIP support 24/7',
                    'Zero network fees',
                    'Custom routing',
                    'Multi-chain flexibility',
                    'Permanent validity'
                ]
            }
        }
    ]

    const calculatePrice = (amount: number) => {
        return (amount * 0.20).toFixed(2)
    }

    const handlePackageSelect = (pkg: Package) => {
        // Toggle if already selected
        if (selectedPackage?.id === pkg.id) {
            setSelectedPackage(null)
        } else {
            setSelectedPackage({
                id: pkg.id,
                name: pkg.name,
                amount: pkg.amount,
                price: pkg.price
            })
            setShowCustom(false)
            setCustomAmount('')
        }
    }

    const handleDevSelect = (id: string, name: string, price: number) => {
        // Toggle if already selected
        if (developerAccess?.id === id) {
            setDeveloperAccess(null)
        } else {
            setDeveloperAccess({
                id,
                name,
                price,
                amount: 0
            })
        }
    }

    const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        if (value === '' || /^\d+$/.test(value)) {
            setCustomAmount(value)
            const numValue = parseInt(value)
            if (numValue >= 10000) {
                setSelectedPackage({
                    id: 'custom',
                    name: 'Custom Amount',
                    amount: numValue,
                    price: parseFloat(calculatePrice(numValue))
                })
            } else {
                setSelectedPackage(null)
            }
        }
    }

    return (
        <div className="pricing-module">
            <div className="pricing-header">
                <h2 className="section-title">{t('pricing.title')} <span className="text-neon">{t('pricing.titleHighlight')}</span></h2>
                <p className="section-subtitle">Choose the plan that fits your needs • Minimum 10,000 USDT</p>
            </div>

            <div className="package-selector">
                {packages.map((pkg, index) => (
                    <motion.button
                        key={pkg.id}
                        className={`package-option glass ${selectedPackage?.id === pkg.id ? 'selected' : ''}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => handlePackageSelect(pkg)}
                    >
                        <div className="package-header">
                            <div className="package-name-section">
                                <h3 className="package-name">{pkg.name}</h3>
                                <div className="package-amount">
                                    <span className="amount-value">{pkg.amount.toLocaleString()}</span>
                                    <span className="amount-label">USDT</span>
                                </div>
                            </div>
                            <div className="package-price">
                                <span className="price-value">${pkg.price.toLocaleString()}</span>
                            </div>
                        </div>


                    </motion.button>
                ))}

                {/* Custom Amount Section */}
                <motion.div
                    className="custom-amount-wrapper"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                >
                    <button
                        className={`custom-toggle-btn package-option glass ${selectedPackage?.id === 'custom' ? 'selected' : ''} ${showCustom ? 'active' : ''}`}
                        onClick={() => {
                            setShowCustom(!showCustom)
                            if (!showCustom) {
                                // Don't auto-select on open, let user type
                            } else {
                                // If closing, maybe deselect if it was custom? 
                                // Actually better to keep logic simple: 
                                if (selectedPackage?.id === 'custom') setSelectedPackage(null)
                            }
                        }}
                    >
                        <div className="package-header">
                            <div className="package-name-section">
                                <h3 className="package-name">{t('pricing.custom')}</h3>
                                <div className="package-amount">
                                    <span className="amount-label">{t('pricing.customPlaceholder')}</span>
                                </div>
                            </div>
                            <span className="toggle-icon">{showCustom ? '−' : '+'}</span>
                        </div>


                    </button>

                    {showCustom && (
                        <motion.div
                            className="custom-input-container glass"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                        >
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="custom-input"
                                    placeholder="Enter amount (min. 10,000)"
                                    value={customAmount}
                                    onChange={handleCustomAmountChange}
                                />
                                <span className="input-suffix">USDT</span>
                            </div>

                            {customAmount && parseInt(customAmount) >= 10000 && (
                                <div className="custom-price">
                                    <span>Estimated Cost:</span>
                                    <span className="text-neon">${calculatePrice(parseInt(customAmount))}</span>
                                </div>
                            )}

                            {customAmount && parseInt(customAmount) < 10000 && (
                                <div className="error-message">
                                    ⚠ Minimum amount is 10,000 USDT
                                </div>
                            )}

                            {customAmount && parseInt(customAmount) > 25000 && (
                                <div className="bulk-warning">
                                    <span className="warning-icon">⚡</span>
                                    <div>
                                        <strong>Bulk Purchase Detected</strong>
                                        <p>For orders above $25,000, please contact our admin team for manual processing and better rates.</p>
                                        <a href="#" className="contact-link">Contact Admin →</a>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    )}
                </motion.div>

                {/* Developer Access Section */}
                <div className="developer-section">
                    <h3 className="dev-section-title">{t('pricing.devAccess.title')}</h3>
                    <div className="dev-options">
                        <motion.button
                            className={`package-option glass dev-option ${developerAccess?.id === 'api-access' ? 'selected' : ''}`}
                            whileHover={{ scale: 1.02 }}
                            onClick={() => handleDevSelect('api-access', 'API Access', 999)}
                        >
                            <div className="package-header">
                                <div className="package-name-section">
                                    <h3 className="package-name text-neon-blue">{t('pricing.devAccess.apiAccess')}</h3>
                                    <span className="amount-label">Monthly Subscription</span>
                                </div>
                                <div className="package-price">
                                    <span className="price-value">$999</span>
                                </div>
                            </div>

                        </motion.button>

                        <motion.button
                            className={`package-option glass dev-option ${developerAccess?.id === 'source-code' ? 'selected' : ''}`}
                            whileHover={{ scale: 1.02 }}
                            onClick={() => handleDevSelect('source-code', 'Full Source Code', 15000)}
                        >
                            <div className="package-header">
                                <div className="package-name-section">
                                    <h3 className="package-name text-neon-purple">Full Source Code</h3>
                                    <span className="amount-label">One-Time License</span>
                                </div>
                                <div className="package-price">
                                    <span className="price-value">$15k</span>
                                </div>
                            </div>

                        </motion.button>
                    </div>
                </div>
            </div>

            <AnimatePresence mode="wait">
                {selectedPackage && selectedPackage.id !== 'custom' && (
                    <motion.div
                        key={selectedPackage.id}
                        className="package-details glass"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {packages.find(p => p.id === selectedPackage.id) && (
                            <>
                                <h4 className="details-title">{t('pricing.package')} Features</h4>
                                <div className="features-grid">
                                    <div className="feature-item">
                                        <span className="feature-label">Delivery:</span>
                                        <span className="feature-value text-neon">
                                            {packages.find(p => p.id === selectedPackage.id)!.features.delivery}
                                        </span>
                                    </div>

                                    <div className="feature-item">
                                        <span className="feature-label">Wallet Duration:</span>
                                        <span className="feature-value">
                                            {packages.find(p => p.id === selectedPackage.id)!.features.walletDuration}
                                        </span>
                                    </div>

                                    <div className="feature-item">
                                        <span className="feature-label">Transferable:</span>
                                        <span className="feature-value">
                                            {packages.find(p => p.id === selectedPackage.id)!.features.transferable}
                                        </span>
                                    </div>

                                    <div className="feature-item">
                                        <span className="feature-label">Swappable:</span>
                                        <span className={`feature-value ${packages.find(p => p.id === selectedPackage.id)!.features.swappable ? 'text-neon' : 'text-muted'}`}>
                                            {packages.find(p => p.id === selectedPackage.id)!.features.swappable ? '✓ Yes' : '✗ No'}
                                        </span>
                                    </div>

                                    <div className="feature-item">
                                        <span className="feature-label">Tradable:</span>
                                        <span className={`feature-value ${packages.find(p => p.id === selectedPackage.id)!.features.tradable ? 'text-neon' : 'text-muted'}`}>
                                            {packages.find(p => p.id === selectedPackage.id)!.features.tradable ? '✓ Yes' : '✗ No'}
                                        </span>
                                    </div>

                                    <div className="feature-item">
                                        <span className="feature-label">Web3 Compatible:</span>
                                        <span className="feature-value text-neon">
                                            {packages.find(p => p.id === selectedPackage.id)!.features.web3Compatible ? '✓ Yes' : '✗ No'}
                                        </span>
                                    </div>

                                    <div className="feature-item full-width">
                                        <span className="feature-label">Confirmations:</span>
                                        <span className="feature-value">
                                            {packages.find(p => p.id === selectedPackage.id)!.features.confirmations}
                                        </span>
                                    </div>
                                </div>

                                {packages.find(p => p.id === selectedPackage.id)!.features.additionalFeatures && (
                                    <div className="additional-features">
                                        <h5>Additional Benefits:</h5>
                                        <ul>
                                            {packages.find(p => p.id === selectedPackage.id)!.features.additionalFeatures!.map((feature, i) => (
                                                <li key={i}>
                                                    <span className="bullet">▸</span>
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
